import { useCallback, useContext, useState } from 'react';
import { AppContext } from 'Context';

// The product default background is the Soft Aurora — the /access backdrop:
// blue/violet shader bands over black (backend stores the string
// "default"/empty). This solid is only used as a dark fallback for preview
// surfaces that need a single colour — it mirrors the effect's base so
// "Default" still reads as a dark backdrop (light ink).
export const DEFAULT_BG_COLOR = '#000';

// The /access "Soft Aurora" backdrop offered as a projector theme: black base
// with the blue/violet SoftAuroraGL bands over it. Stored as this sentinel
// rather than a CSS colour, so anything that paints a background has to route
// through bgSurface()/isFxBg() below instead of dropping it straight into
// `background`.
export const PROFILE_BG_SOFT_AURORA = 'soft-aurora';

// The original projector backdrop, kept as a theme of its own: three very dark
// colour fields (navy / violet / steel blue) bleeding into a near-black base,
// with the dimmed Aurora shader on top. Components/viewer-inner re-exports the
// gradient as DEFAULT_PROJECTOR_BG and draws the animated half
// (ProjectorAuroraFX); the string lives here so this module stays the single
// source for "what does this stored value paint" without importing back.
export const PROFILE_BG_DEEP_AURORA = 'deep-aurora';
export const DEEP_AURORA_BG = [
  'radial-gradient(85% 60% at 12% -5%, rgba(32, 54, 102, 0.55) 0%, rgba(32, 54, 102, 0) 62%)',
  'radial-gradient(70% 55% at 92% 4%, rgba(66, 46, 116, 0.46) 0%, rgba(66, 46, 116, 0) 64%)',
  'radial-gradient(95% 70% at 46% 112%, rgba(16, 40, 72, 0.52) 0%, rgba(16, 40, 72, 0) 66%)',
  'radial-gradient(130% 105% at 50% 42%, #0a0c14 0%, #06070c 52%, #030407 100%)',
].join(', ');

// Which live effect a stored value means, if any. '' is Default, which IS the
// Soft Aurora now — the explicit sentinel is still honoured for accounts that
// already saved it. Everything else is a plain CSS colour.
export const fxKind = (value) => {
  const v = String(value || '');
  if (v === '' || v === PROFILE_BG_SOFT_AURORA) return 'soft';
  if (v === PROFILE_BG_DEEP_AURORA) return 'deep';
  return null;
};

// Backgrounds that are a live effect rather than a paintable CSS value.
export const isFxBg = (value) => fxKind(value) !== null;

// What a surface should actually paint UNDER an effect (or instead of one for a
// plain preset): black for the Soft Aurora, its own gradient for the deep one.
export const bgSurface = (value) => {
  const kind = fxKind(value);
  if (kind === 'soft') return DEFAULT_BG_COLOR;
  if (kind === 'deep') return DEEP_AURORA_BG;
  return value;
};

// Miniature stand-ins for swatches/preview chips that can only take a CSS value.
export const DEFAULT_BG_SWATCH = 'radial-gradient(circle at 30% 26%, #5aa5ef 0%, #6d5efc 42%, #10132a 74%, #000 100%)';
export const DEEP_BG_SWATCH = 'radial-gradient(circle at 24% 20%, #2a3a66 0%, #1d2142 38%, #0a0c14 72%, #030407 100%)';
export const bgSwatch = (value) => {
  const kind = fxKind(value);
  if (kind === 'soft') return DEFAULT_BG_SWATCH;
  if (kind === 'deep') return DEEP_BG_SWATCH;
  return value;
};

// A flat single colour for surfaces that can't paint a gradient or run a
// shader (small preview chips, canvas fills).
export const bgSolid = (value) => (isFxBg(value) ? DEFAULT_BG_COLOR : value);

// Preset background colours a user can pick for their Projector (/profile).
// '' = Default — the Soft Aurora effect (see isFxBg) — first swatch.
export const PROFILE_BG_DEFAULT = { name: 'Default (Soft Aurora)', value: '' };
export const PROFILE_BG_PRESETS = [
  { name: 'Deep Aurora', value: PROFILE_BG_DEEP_AURORA },
  { name: 'Brick', value: '#5c1f1f' },
  { name: 'Oxblood', value: '#3a1414' },
  { name: 'Graphite', value: '#2b2b2f' },
  { name: 'Charcoal', value: '#141417' },
  { name: 'Plum', value: '#241a35' },
  { name: 'Forest', value: '#12241c' },
  { name: 'Deep blue', value: '#1e3a5f' },
  { name: 'Paper', value: '#eef0f5' },
  { name: 'Lilac', value: '#e7dffb' },
];

// TEMPORARY storage: keyed by the logged-in user's id in localStorage so the
// choice on the Layout tab is reflected on that user's own /profile for testing.
// The real per-owner value (returned when anyone views /profile/:ownerId) will
// come from the backend later — swap read()/write() for that field then.
const key = () => {
  try {
    return `projector.profileBg.${localStorage.getItem('id') || 'anon'}`;
  } catch (e) {
    return 'projector.profileBg.anon';
  }
};

export const readProfileBg = () => {
  try {
    return localStorage.getItem(key()) || '';
  } catch (e) {
    return '';
  }
};

// Pull projector_background out of an API `settings` payload, which may arrive
// as an array (settings[0]), an object, or a flat field on the record. The
// backend sends the string "default" for the animated aurora — normalise that
// (and empty/whitespace) to '' so callers render the Default aurora.
export const normalizeBg = (val) => {
  const v = String(val || '').trim();
  return v.toLowerCase() === 'default' ? '' : v;
};

const pickBg = (record) => {
  if (!record) return '';
  const s = record.settings;
  const fromSettings = Array.isArray(s) ? s[0]?.projector_background : s?.projector_background;
  return normalizeBg(fromSettings || record.projector_background || '');
};

const readMyId = () => {
  try { return localStorage.getItem('id') || ''; } catch (e) { return ''; }
};

/**
 * Resolve the projector background for the owner whose page is being viewed,
 * sourced from the APIs the app already loads:
 *   • another projector  → that connection in getAllViewRequestSent (userConnections)
 *   • self / own profile → getMyProfile (userDetails.settings), then the local
 *                          Layout-tab preview so the owner sees edits live.
 * Returns '' for "Default" (the animated dark-blue aurora).
 */
export const useProjectorBg = (profileID) => {
  const { userConnections, userDetails } = useContext(AppContext) || {};
  const isOther = profileID && String(profileID) !== String(userDetails?.myId || readMyId());
  if (isOther) {
    const conn = (userConnections || []).find((c) => String(c.id) === String(profileID));
    return pickBg(conn);
  }
  // Own projector: live local preview wins, then the API value.
  return readProfileBg() || pickBg(userDetails);
};

export const useProfileBg = () => {
  const [color, setColorState] = useState(readProfileBg);

  const setColor = useCallback((c) => {
    setColorState(c || '');
    try {
      if (c) localStorage.setItem(key(), c);
      else localStorage.removeItem(key());
    } catch (e) { /* noop */ }
    // Let a mounted /profile in the same tab react immediately if it listens.
    try { window.dispatchEvent(new CustomEvent('profileBg:change', { detail: c || '' })); } catch (e) { /* noop */ }
  }, []);

  return { color, setColor };
};

export default useProfileBg;
